import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import { platformAPI } from '../../../services/platformAPI';
import { userHasPermission } from '../../../utils/permissions';
import './TimesheetsPage.scss';

const LEGACY_ACTIVITIES = {
  bid_prep: 'Bid preparation',
  site_visit: 'Site visit',
  costing: 'Costing',
  technical: 'Technical response',
  meeting: 'Client / team meeting',
  admin: 'Administration'
};

const StatusBadge = ({ status }) => {
  const key = status || 'draft';
  const label = key.charAt(0).toUpperCase() + key.slice(1);
  return <span className={`ts-page__badge ts-page__badge--${key}`}>{label}</span>;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatWorkspaceDate(date);
};

const TimesheetsPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const myId = user?._id || user?.id;
  const [permissions, setPermissions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mineOnly, setMineOnly] = useState(true);
  const [status, setStatus] = useState('');
  const [workType, setWorkType] = useState('');
  const [activityId, setActivityId] = useState('');
  const [activities, setActivities] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const canSubmit = userHasPermission(permissions, 'tender.timesheet.submit');

  useDocumentTitle('Timesheets');

  useEffect(() => {
    platformAPI.bootstrap()
      .then((response) => setPermissions(response.data?.data?.permissions || []))
      .catch(() => setPermissions([]));
    platformAPI.timesheetMeta()
      .then((response) => setActivities(response.data?.data?.activities || []))
      .catch(() => setActivities([]));
  }, []);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const response = await platformAPI.listTimesheets({
        mine: mineOnly ? 'true' : undefined,
        status: status || undefined,
        workType: workType || undefined,
        activityId: activityId || undefined
      });
      setRows(response.data?.data?.timesheets || []);
      setTotals(response.data?.data?.totals || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load timesheets');
      setRows([]);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [mineOnly, status, workType, activityId]);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'timesheet',
    noun: 'timesheet',
    plural: 'timesheets',
    reload: load,
    label: (row) => row.activityName || row.personName || 'Timesheet'
  });

  const tableRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      dateLabel: formatDate(row.workDate),
      activityLabel: row.activityName || LEGACY_ACTIVITIES[row.activityCategory] || row.activityCategory || '—',
      tenderLabel: row.workType === 'assignment'
        ? (row.assignmentName || row.assignmentReference || 'Assignment')
        : (row.tenderReference ? `${row.tenderReference}${row.tenderTitle ? ` — ${row.tenderTitle}` : ''}` : '—'),
      personLabel: row.personName || '—'
    })),
    [rows]
  );

  const columns = useMemo(
    () => [
      { key: 'dateLabel', label: 'Date', width: '140px', mobileTitle: true },
      { key: 'personLabel', label: 'Person' },
      { key: 'workType', label: 'Work', width: '120px', render: (value) => (value === 'assignment' ? 'Assignment' : 'Tender') },
      { key: 'tenderLabel', label: 'Tender / assignment' },
      { key: 'activityLabel', label: 'Activity' },
      {
        key: 'hours',
        label: 'Hours',
        width: '90px',
        render: (value) => <span className="ts-page__hours">{value}h</span>
      },
      {
        key: 'status',
        label: 'Status',
        width: '130px',
        render: (value) => <StatusBadge status={value} />
      }
    ],
    []
  );

  const openRecord = (row, mode) => {
    const id = row._id || row.id;
    if (!id) return;
    navigate(mode === 'edit' ? `/apps/tender/timesheets/${id}/edit` : `/apps/tender/timesheets/${id}`);
  };

  const counts = totals?.byStatus || {};
  const statusCards = [
    { id: 'draft', label: 'Draft', tone: '' },
    { id: 'submitted', label: 'Submitted', tone: 'info' },
    { id: 'returned', label: 'Returned', tone: 'warn' },
    { id: 'approved', label: 'Approved', tone: 'success' }
  ];
  const selectStatus = (next) => setStatus((current) => (current === next ? '' : next));

  if (!loadedOnce && loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="ts-page">
      <WorkspacePageHeader
        title="Timesheets"
        help="timesheets"
        subtitle="Effort against a tender or an awarded assignment. Drafts and returned entries can be corrected. Approved entries stay locked."
        actions={canSubmit ? (
          <>
            <Button size="sm" variant="outline-primary" onClick={() => navigate('/apps/tender/timesheets/week')}>
              Week
            </Button>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/timesheets/new')}>
              New entry
            </Button>
          </>
        ) : null}
      />
      <section className="ts-command" aria-label="Timesheet summary and filters">
        <div className="ts-page__stats">
          <div className="ts-page__stat ts-page__stat--primary">
            <div className="ts-page__stat-value">{totals?.hours || 0}<span>h</span></div>
            <div className="ts-page__stat-label">Hours in view</div>
            <p className="ts-page__stat-hint">{loading ? 'Updating…' : 'Across the entries below'}</p>
          </div>
          {statusCards.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`ts-page__stat${card.tone ? ` ts-page__stat--${card.tone}` : ''}${status === card.id ? ' is-on' : ''}`}
              onClick={() => selectStatus(card.id)}
              aria-pressed={status === card.id}
            >
              <div className="ts-page__stat-value">{counts[card.id] || 0}</div>
              <div className="ts-page__stat-label">{card.label}</div>
              <p className="ts-page__stat-hint">{status === card.id ? 'Showing' : 'Entries'}</p>
            </button>
          ))}
        </div>
        <div className="ts-filters">
          <label className="ts-filters__field">
            <span className="ts-filters__label">Status</span>
            <Form.Select size="sm" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Status">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="returned">Returned</option>
              <option value="approved">Approved</option>
            </Form.Select>
          </label>
          <label className="ts-filters__field">
            <span className="ts-filters__label">Work</span>
            <Form.Select size="sm" value={workType} onChange={(event) => setWorkType(event.target.value)} aria-label="Work type">
              <option value="">Tender and assignment</option>
              <option value="tender">Tender</option>
              <option value="assignment">Assignment</option>
            </Form.Select>
          </label>
          <label className="ts-filters__field ts-filters__activity">
            <span className="ts-filters__label">Activity</span>
            <Form.Select size="sm" value={activityId} onChange={(event) => setActivityId(event.target.value)} aria-label="Activity">
              <option value="">All activities</option>
              {activities.map((activity) => (
                <option key={activity._id} value={activity._id}>{activity.name}</option>
              ))}
            </Form.Select>
          </label>
          <div className="ts-filters__mine">
            <Form.Check
              type="switch"
              id="ts-mine-only"
              label="Only my entries"
              checked={mineOnly}
              onChange={(event) => setMineOnly(event.target.checked)}
            />
          </div>
        </div>
      </section>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="timesheets.download"
        title="Timesheet entries"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search person, tender, activity…"
        pageSize={12}
        onView={(row) => openRecord(row, 'view')}
        onEdit={(row) => openRecord(row, 'edit')}
        {...admin.tableProps}
        rowActions={admin.wrapActions((row) => {
          const own = String(row.userId) === String(myId);
          const editable = own && ['draft', 'returned'].includes(row.status);
          return editable
            ? [{ type: 'view', label: 'View' }, { type: 'edit', label: 'Edit' }]
            : [{ type: 'view', label: 'View' }];
        })}
      />
      {admin.modal}
    </div>
  );
};

export default TimesheetsPage;
